import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getEscalationInstance } from '../services/escalationRuntimeService';
import {
  acknowledgeCriticalNotification,
  getCriticalNotification,
  getCriticalNotificationEscalationHistory,
  getCriticalNotificationHistory,
  getCriticalNotificationReminderHistory,
} from '../services/criticalNotificationService';
import type {
  CriticalNotification,
  CriticalNotificationDetailContext,
  NotificationHistoryEvent,
  NotificationHistoryTab,
} from '../types/criticalNotification.types';
import {
  filterEscalationHistoryEvents,
  filterReminderHistoryEvents,
  sortHistoryEventsChronologically,
} from '../utils/criticalNotificationUtils';

const HISTORY_PAGE_SIZE = 50;

async function loadReminderTabEvents(args: {
  notificationId: number;
  facilityId: number;
  params: { limit: number; offset: number };
  fullHistory: NotificationHistoryEvent[];
  isStale: () => boolean;
  setReminderEvents: (events: NotificationHistoryEvent[]) => void;
  setHistoryError: (error: string | null) => void;
}): Promise<void> {
  const result = await getCriticalNotificationReminderHistory(
    args.notificationId,
    args.facilityId,
    args.params
  );
  if (args.isStale()) {
    return;
  }

  if (result.success && result.data.length > 0) {
    args.setReminderEvents(sortHistoryEventsChronologically(result.data));
    return;
  }
  args.setReminderEvents(filterReminderHistoryEvents(args.fullHistory));
  if (!result.success && !args.fullHistory.length) {
    args.setHistoryError(result.error || 'Failed to load reminder history');
  }
}

async function loadEscalationTabEvents(args: {
  notificationId: number;
  facilityId: number;
  params: { limit: number; offset: number };
  fullHistory: NotificationHistoryEvent[];
  isStale: () => boolean;
  setEscalationEvents: (events: NotificationHistoryEvent[]) => void;
  setHistoryError: (error: string | null) => void;
}): Promise<void> {
  const result = await getCriticalNotificationEscalationHistory(
    args.notificationId,
    args.facilityId,
    args.params
  );
  if (args.isStale()) {
    return;
  }

  if (result.success && result.data.length > 0) {
    args.setEscalationEvents(sortHistoryEventsChronologically(result.data));
    return;
  }
  args.setEscalationEvents(filterEscalationHistoryEvents(args.fullHistory));
  if (!result.success && !args.fullHistory.length) {
    args.setHistoryError(result.error || 'Failed to load escalation history');
  }
}

interface UseCriticalNotificationDetailOptions {
  open: boolean;
  context: CriticalNotificationDetailContext | null;
  onAcknowledged?: () => void;
}

export function useCriticalNotificationDetail({
  open,
  context,
  onAcknowledged,
}: UseCriticalNotificationDetailOptions) {
  const [notification, setNotification] = useState<CriticalNotification | null>(null);
  const [resolvedNotificationId, setResolvedNotificationId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<NotificationHistoryTab>('timeline');
  const [timelineEvents, setTimelineEvents] = useState<NotificationHistoryEvent[]>([]);
  const [reminderEvents, setReminderEvents] = useState<NotificationHistoryEvent[]>([]);
  const [escalationEvents, setEscalationEvents] = useState<NotificationHistoryEvent[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [ackError, setAckError] = useState<string | null>(null);
  const historyRequestIdRef = useRef(0);

  const resetState = useCallback(() => {
    historyRequestIdRef.current += 1;
    setNotification(null);
    setResolvedNotificationId(null);
    setActiveTab('timeline');
    setTimelineEvents([]);
    setReminderEvents([]);
    setEscalationEvents([]);
    setDetailLoading(false);
    setHistoryLoading(false);
    setAcknowledging(false);
    setDetailError(null);
    setHistoryError(null);
    setAckError(null);
  }, []);

  const resolveNotificationId = useCallback(async () => {
    if (!context) {
      return null;
    }
    if (context.criticalNotificationId) {
      return context.criticalNotificationId;
    }

    const instanceResult = await getEscalationInstance(
      context.escalationInstanceId,
      context.facilityId
    );
    if (!instanceResult.success || !instanceResult.data) {
      return null;
    }

    return instanceResult.data.criticalNotification?.id ?? null;
  }, [context]);

  const loadDetail = useCallback(async () => {
    if (!context) {
      return;
    }
    setDetailLoading(true);
    setDetailError(null);

    const notificationId = await resolveNotificationId();
    setResolvedNotificationId(notificationId);

    if (!notificationId) {
      setNotification(null);
      setDetailLoading(false);
      setDetailError('No closed-loop notification record is linked to this alert.');
      return;
    }

    const result = await getCriticalNotification(notificationId, context.facilityId);
    if (!result.success || !result.data) {
      setNotification(null);
      setDetailError(result.error || 'Failed to load notification details');
    } else {
      setNotification(result.data);
    }
    setDetailLoading(false);
  }, [context, resolveNotificationId]);

  const loadHistoryForTab = useCallback(
    async (tab: NotificationHistoryTab, notificationId: number) => {
      if (!context) {
        return;
      }

      const requestId = ++historyRequestIdRef.current;
      setHistoryLoading(true);
      setHistoryError(null);

      const params = { limit: HISTORY_PAGE_SIZE, offset: 0 };
      const isStale = () => requestId !== historyRequestIdRef.current;

      try {
        const historyResult = await getCriticalNotificationHistory(
          notificationId,
          context.facilityId,
          params
        );

        if (isStale()) {
          return;
        }

        const fullHistory = historyResult.success
          ? sortHistoryEventsChronologically(historyResult.data || [])
          : [];

        setTimelineEvents(fullHistory);

        if (!historyResult.success && tab === 'timeline') {
          setHistoryError(historyResult.error || 'Failed to load notification history');
        }

        if (tab === 'timeline') {
          return;
        }

        if (tab === 'reminders') {
          await loadReminderTabEvents({
            notificationId,
            facilityId: context.facilityId,
            params,
            fullHistory,
            isStale,
            setReminderEvents,
            setHistoryError,
          });
          return;
        }

        if (tab === 'escalation') {
          await loadEscalationTabEvents({
            notificationId,
            facilityId: context.facilityId,
            params,
            fullHistory,
            isStale,
            setEscalationEvents,
            setHistoryError,
          });
        }
      } finally {
        if (!isStale()) {
          setHistoryLoading(false);
        }
      }
    },
    [context]
  );

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    void loadDetail();
  }, [open, context?.escalationInstanceId, context?.facilityId, loadDetail, resetState]);

  useEffect(() => {
    if (!open || !resolvedNotificationId) {
      return;
    }
    void loadHistoryForTab(activeTab, resolvedNotificationId);
  }, [open, activeTab, resolvedNotificationId, loadHistoryForTab]);

  const activeEvents = useMemo(() => {
    switch (activeTab) {
      case 'reminders':
        return reminderEvents;
      case 'escalation':
        return escalationEvents;
      default:
        return timelineEvents;
    }
  }, [activeTab, timelineEvents, reminderEvents, escalationEvents]);

  const handleAcknowledge = useCallback(async () => {
    if (!context || !resolvedNotificationId) {
      return;
    }
    setAcknowledging(true);
    setAckError(null);
    const result = await acknowledgeCriticalNotification(
      resolvedNotificationId,
      context.facilityId,
      'API'
    );
    setAcknowledging(false);
    if (!result.success) {
      setAckError(result.error || 'Failed to acknowledge notification');
      return;
    }
    if (result.data) {
      setNotification(result.data);
    }
    await loadHistoryForTab(activeTab, resolvedNotificationId);
    onAcknowledged?.();
  }, [context, resolvedNotificationId, activeTab, loadHistoryForTab, onAcknowledged]);

  const refresh = useCallback(async () => {
    await loadDetail();
    if (resolvedNotificationId) {
      await loadHistoryForTab(activeTab, resolvedNotificationId);
    }
  }, [loadDetail, loadHistoryForTab, activeTab, resolvedNotificationId]);

  return {
    notification,
    resolvedNotificationId,
    activeTab,
    setActiveTab,
    activeEvents,
    detailLoading,
    historyLoading,
    acknowledging,
    detailError,
    historyError,
    ackError,
    handleAcknowledge,
    refresh,
  };
}
